import { CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
// Component imports
import { AutoColumn } from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import FiatValue from 'components/FiatValue'
import { CharmWarningBanner, YbgtWarningBanner } from 'components/IslandWarningCard'
import Loader from 'components/Loader'
import MigrationBanner from 'components/MigrationBanner'
import PoolNotFoundUI from 'components/PoolNotFound'
import PoweredByKodiak from 'components/PoweredByKodiak'
import { HeaderWithPriceInfo } from 'components/PriceRange'
import { RowBetween } from 'components/Row'
// Hook imports
import { isSupportedChain } from 'config/chains'
import { ISLAND_ROUTER_ADDRESSES, V2_ROUTER_ADDRESSES } from 'constants/addresses'
import { useTokens } from 'hooks/Tokens'
import { useBaultDatas } from 'hooks/useBault'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import { useKodiakFarmByPool, useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import {
  useOptimizedPoolPositionDetails,
  useOptimizedPoolUnderlyingBalances,
  useSharedUnderlyingBalances,
} from 'hooks/usePoolPositionDetails'
import { useProcessedPoolDataWithFallback } from 'hooks/useProcessedPoolData'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import { usePool } from 'pages/Liquidity/hooks/usePool'
// Configuration imports
// import { usePoolConfiguration } from 'pools/config'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { useLocation, useParams } from 'react-router-dom'
import { IslandApiResponse } from 'state/data/slice'
import styled, { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { CopyLinkIcon, ExternalLink, StyledInternalLink } from 'theme/components'
import { KodiakFarm } from 'types/farms'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'
import { unwrappedToken } from 'utils/unwrappedToken'
import { isAddress } from 'viem'

import useNativeCurrency from '../../../lib/hooks/useNativeCurrency'
import { CharmDepositCapCard } from './CharmDepositCapCard'
import {
  AprDisplayConfig,
  PoolConfig,
  PoolExternalLink,
  PoolMetrics,
  StakingData,
  usePoolConfiguration,
} from './config'
// Context and components
import { IslandContextType } from './context/IslandContextType'
import { IslandProvider } from './context/IslandProvider'
import IslandAnalyticsCard from './IslandAnalyticsCard'
import IslandFarmEarnedCard from './IslandFarmEarnedCard'
import IslandManagementCard from './IslandManagementCard'
import IslandPositionCard from './IslandPositionCard'
import * as S from './styleds'

const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.deprecated_text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  `};
`

const StategyBadge = styled.div`
  background-color: ${({ theme }) => theme.cardSecondary};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 20px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`

interface PoolPageProps {
  hasKodiakFarm?: boolean
  tokenId?: string
  kodiakFarmAddress?: string
  originalKodiakFarmAddress?: string
  rewardVaultAddress?: string
  isWhitelistedForRewardVault?: boolean
  isRewardVaultView: boolean
  routeType?: string
  poolApiResponse?: IslandApiResponse
  isLoading?: boolean
}

const AprCard: React.FC<{
  config: PoolConfig
  metrics: PoolMetrics
  aprDisplay: AprDisplayConfig
  stakingData: StakingData
  farm?: KodiakFarm
  poolApiResponse: IslandApiResponse | null | undefined
}> = ({ aprDisplay, stakingData, farm, poolApiResponse }) => {
  const theme = useContext(ThemeContext)

  const rewardVaultRewardApr = useMemo<number | null>(() => {
    if (
      stakingData.stakingType === 'rewardVault' &&
      poolApiResponse?.farm?.apr !== null &&
      poolApiResponse?.farm?.apr !== undefined &&
      poolApiResponse?.isRewardVault === true
    ) {
      return poolApiResponse.farm.apr
    }
    return null
  }, [stakingData.stakingType, poolApiResponse])

  const aprData = useMemo(() => {
    if (!aprDisplay.showKodiakFarmApr && !aprDisplay.showRewardVaultRewardApr) {
      return { shouldShow: false, apr: null, isLoading: false }
    }

    if (
      stakingData.stakingType === 'rewardVault' &&
      aprDisplay.showRewardVaultRewardApr &&
      rewardVaultRewardApr !== null
    ) {
      return {
        shouldShow: true,
        apr: rewardVaultRewardApr,
        isLoading: false,
      }
    }

    if (stakingData.stakingType === 'kodiakFarm' && aprDisplay.showKodiakFarmApr) {
      return {
        shouldShow: true,
        apr: farm?.apr,
        isLoading: false,
      }
    }

    return { shouldShow: false, apr: null, isLoading: false }
  }, [
    aprDisplay.showKodiakFarmApr,
    aprDisplay.showRewardVaultRewardApr,
    stakingData.stakingType,
    rewardVaultRewardApr,
    farm?.apr,
  ])

  if (!aprData.shouldShow || aprData.apr === null) {
    return null
  }

  if (aprData.apr === 0 && stakingData.stakingType === 'none') {
    return null
  }

  return (
    <S.IslandInfoInnerBox>
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
        {aprDisplay.farmLabel}
      </ThemedText.DeprecatedBody>
      <ThemedText.DeprecatedBody
        fontSize="16px"
        fontWeight="400"
        lineHeight="28px"
        color={theme.textPrimary}
        style={{
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {aprData.isLoading ? <Loader /> : `${formatAmount(aprData.apr?.toFixed(2) ?? '0.00')}%`}
      </ThemedText.DeprecatedBody>
    </S.IslandInfoInnerBox>
  )
}

const LinkWithCopy = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ExternalLinksSection: React.FC<{ externalLinks: PoolExternalLink[] }> = ({ externalLinks }) => {
  if (externalLinks.length === 0) return null
  return (
    <S.FlexRowCenter>
      {externalLinks.map((link, index) => {
        return (
          <LinkWithCopy key={index}>
            <ExternalLink href={link.url}>{link.label} ↗</ExternalLink>
            {link.copyText && <CopyLinkIcon toCopy={link.copyText} />}
          </LinkWithCopy>
        )
      })}
    </S.FlexRowCenter>
  )
}

const PoolMetricsSection: React.FC<{
  config: PoolConfig
  metrics: PoolMetrics
  stakingData: StakingData
  aprDisplay: AprDisplayConfig
  kodiakFarm?: KodiakFarm
  poolApiResponse: IslandApiResponse | null | undefined
  displayFarmTvl?: number
}> = ({ config, metrics, stakingData, aprDisplay, kodiakFarm, poolApiResponse, displayFarmTvl }) => {
  const theme = useContext(ThemeContext)

  return (
    <S.FlexRowCenter style={{ flexWrap: 'wrap' }}>
      <S.IslandInfoInnerBox>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
          Pool TVL
        </ThemedText.DeprecatedBody>
        <ThemedText.DeprecatedBody fontSize="16px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
          <FiatValue value={metrics.tvl} />
        </ThemedText.DeprecatedBody>
      </S.IslandInfoInnerBox>

      {displayFarmTvl && (stakingData.stakingType === 'kodiakFarm' || stakingData.stakingType === 'rewardVault') ? (
        <S.IslandInfoInnerBox>
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
            {stakingData.stakingType === 'rewardVault' ? 'Reward Vault TVL' : 'Farm TVL'}
          </ThemedText.DeprecatedBody>
          <ThemedText.DeprecatedBody fontSize="16px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
            <FiatValue value={displayFarmTvl} />
          </ThemedText.DeprecatedBody>
        </S.IslandInfoInnerBox>
      ) : undefined}

      {aprDisplay.showBaseApr && (
        <S.IslandInfoInnerBox>
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
            Pool APR
          </ThemedText.DeprecatedBody>
          <ThemedText.DeprecatedBody fontSize="16px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
            {formatAmount(metrics.baseApr)}%
          </ThemedText.DeprecatedBody>
        </S.IslandInfoInnerBox>
      )}

      {(aprDisplay.showKodiakFarmApr && stakingData.stakingType === 'kodiakFarm') ||
      (aprDisplay.showRewardVaultRewardApr && stakingData.stakingType === 'rewardVault') ? (
        <AprCard
          config={config}
          metrics={metrics}
          aprDisplay={aprDisplay}
          stakingData={stakingData}
          farm={kodiakFarm}
          poolApiResponse={poolApiResponse}
        />
      ) : null}

      {config.type === 'Charm' && <CharmDepositCapCard poolAddress={poolApiResponse?.id} tvlUSD={metrics.tvl} />}

      {config.type !== 'Charm' && (
        <S.IslandInfoInnerBox>
          <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
            Fees (7D)
          </ThemedText.DeprecatedBody>
          <ThemedText.DeprecatedBody fontSize="16px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
            {formatDollarAmount(metrics.weeklyFees || 0)}
          </ThemedText.DeprecatedBody>
        </S.IslandInfoInnerBox>
      )}
    </S.FlexRowCenter>
  )
}

function WrongNetworkCard() {
  const theme = useContext(ThemeContext)
  return (
    <>
      <S.Wrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow style={{ marginTop: '1rem' }} padding="0">
              <ThemedText.DeprecatedBody fontSize="20px">Islands Overview</ThemedText.DeprecatedBody>
            </TitleRow>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                Your connected network is unsupported.
              </ThemedText.DeprecatedBody>
            </div>
          </AutoColumn>
        </AutoColumn>
      </S.Wrapper>
      <PoweredByKodiak />
    </>
  )
}

function PoolPage({
  hasKodiakFarm,
  tokenId: injectedTokenId,
  kodiakFarmAddress: injectedKodiakFarmAddress,
  rewardVaultAddress: injectedRewardVaultAddress,
  originalKodiakFarmAddress,
  isRewardVaultView,
  isWhitelistedForRewardVault = false,
  poolApiResponse,
  isLoading: apiIsLoading = false,
}: PoolPageProps) {
  const { tokenId: urlTokenId } = useParams<{ tokenId?: string }>()
  const { chainId, account } = useConnection()
  const wrappedNativeCurrency = useNativeCurrency().wrapped
  const { price } = useGetTokenPrice(wrappedNativeCurrency.address)
  const theme = useContext(ThemeContext)
  const [stableLoadingState, setStableLoadingState] = useState(true)
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const rawTokenId = useMemo(() => injectedTokenId || urlTokenId, [injectedTokenId, urlTokenId])

  const tokenId = useMemo(() => {
    if (!rawTokenId) return undefined
    return isAddress(rawTokenId) ? rawTokenId : undefined
  }, [rawTokenId])

  const navigate = useChainAwareNavigate()

  const { error, isError, isFetching, isLoading, isUninitialized, pool: _pool, refetch } = usePool(tokenId, price)

  const pool = useMemo(() => {
    if (apiIsLoading) {
      return null
    }

    return poolApiResponse || _pool
  }, [apiIsLoading, poolApiResponse, _pool])

  const shouldShowLoading = useMemo(() => {
    return apiIsLoading || stableLoadingState
  }, [apiIsLoading, stableLoadingState])

  const sharedBalances = useSharedUnderlyingBalances(pool ?? null)

  const { config, poolData, metrics } = usePoolConfiguration(pool ?? undefined)

  const rewardVaultAddress = useMemo(() => {
    const addr = injectedRewardVaultAddress
    return addr && isAddress(addr) ? addr : undefined
  }, [injectedRewardVaultAddress])

  const isRewardVaultStakingDisabled = useMemo(
    () => isRewardVaultView && poolApiResponse?.farm?.isActive === false,
    [isRewardVaultView, poolApiResponse?.farm?.isActive]
  )

  const kodiakFarmAddress = useMemo(() => {
    if (isRewardVaultView) {
      return undefined
    }
    const addr = searchParams.get('farm')
    return addr && isAddress(addr) ? addr : undefined
  }, [isRewardVaultView, searchParams])

  const migrationKodiakFarmAddress = useMemo(() => {
    if (isRewardVaultView) {
      const addr = originalKodiakFarmAddress || injectedKodiakFarmAddress
      return addr && isAddress(addr) ? addr : undefined
    }
    return undefined
  }, [isRewardVaultView, originalKodiakFarmAddress, injectedKodiakFarmAddress])

  const stakingData = useMemo(() => {
    if (!config) return null

    const result = config.getStakingData({
      poolApiResponse: pool,
      kodiakFarmAddress: kodiakFarmAddress ?? undefined,
      rewardVaultAddress: rewardVaultAddress ?? undefined,
      isWhitelistedForRewardVault,
      migrationKodiakFarmAddress: migrationKodiakFarmAddress ?? undefined,
    })

    return result
  }, [config, pool, kodiakFarmAddress, rewardVaultAddress, isWhitelistedForRewardVault, migrationKodiakFarmAddress])

  const [token0, token1] = useTokens([pool?.token0?.id, pool?.token1?.id])
  const currencyA = useMemo(() => (token0 ? unwrappedToken(token0) : null), [token0])
  const currencyB = useMemo(() => (token1 ? unwrappedToken(token1) : null), [token1])

  const routerAddress = useMemo(() => {
    if (!config || !chainId) return null

    switch (config.type) {
      case 'Island':
        return ISLAND_ROUTER_ADDRESSES[chainId]
      case 'V2':
        return V2_ROUTER_ADDRESSES[chainId]
      case 'Charm':
        return null
      default:
        return V2_ROUTER_ADDRESSES[chainId]
    }
  }, [config, chainId])

  const isRewardVaultActive = useMemo(() => !!stakingData?.rewardVaultAddress, [stakingData?.rewardVaultAddress])
  const isKodiakFarmActive = useMemo(
    () => !!stakingData?.kodiakFarmAddress && !isRewardVaultActive,
    [stakingData?.kodiakFarmAddress, isRewardVaultActive]
  )

  const returnTo = (location.state as { returnTo?: string })?.returnTo
  const backPath = returnTo || (isKodiakFarmActive ? '/liquidity/farms' : '/liquidity/pools')
  const backLabel = backPath.includes('farms') ? 'Farms' : 'Pools'

  const kodiakFarmAddressToFetch = useMemo(() => {
    if (isKodiakFarmActive) {
      return stakingData?.kodiakFarmAddress
    }
    return stakingData?.migrationKodiakFarmAddress
  }, [isKodiakFarmActive, stakingData?.kodiakFarmAddress, stakingData?.migrationKodiakFarmAddress])

  const kodiakFarm = useKodiakFarmByPool(kodiakFarmAddressToFetch)

  // Only fetch reward vault data when we have a rewardVaultAddress when stakingType is rewardVault
  const { isLoading: isLoadingRewardVault, data: rewardVault } = useRewardVaultDatas(
    isRewardVaultActive ? stakingData?.rewardVaultAddress : undefined
  )

  // Only fetch bault data when we have a baultAddress when using Bault with rewardVault
  const { data: baultData, isLoading: baultLoading } = useBaultDatas(
    isRewardVaultActive ? stakingData?.baultAddress : undefined
  )

  const { isLoading: isLoadingFarmLockedStakes, data: kodiakFarmLockedStakesData } = useKodiakFarmLockedStakesOf(
    kodiakFarm,
    account
  )

  const totalKodiakFarmLiquidity = useMemo(
    () => (isKodiakFarmActive ? kodiakFarmLockedStakesData?.totalLiquidity : undefined),
    [isKodiakFarmActive, kodiakFarmLockedStakesData?.totalLiquidity]
  )

  const totalKodiakFarmLiquidityUSD = useMemo(
    () => (isKodiakFarmActive ? kodiakFarmLockedStakesData?.totalLiquidityUSD : undefined),
    [isKodiakFarmActive, kodiakFarmLockedStakesData?.totalLiquidityUSD]
  )

  const kodiakFarmStakes = useMemo(() => {
    return isKodiakFarmActive && kodiakFarmLockedStakesData?.stakes ? kodiakFarmLockedStakesData.stakes : []
  }, [isKodiakFarmActive, kodiakFarmLockedStakesData?.stakes])

  const userHasKodiakFarmPosition =
    kodiakFarmLockedStakesData?.totalLiquidity && kodiakFarmLockedStakesData.totalLiquidity.greaterThan('0')

  const shouldShowMigration = useMemo(() => {
    return (
      isWhitelistedForRewardVault &&
      isRewardVaultView &&
      !isRewardVaultStakingDisabled &&
      userHasKodiakFarmPosition &&
      !!stakingData?.migrationKodiakFarmAddress
    )
  }, [
    isWhitelistedForRewardVault,
    isRewardVaultView,
    isRewardVaultStakingDisabled,
    userHasKodiakFarmPosition,
    stakingData?.migrationKodiakFarmAddress,
  ])

  const tickAtLimit = useIsTickAtLimit(poolData?.feeTier, poolData?.lowerTick, poolData?.upperTick)

  const processedData = useProcessedPoolDataWithFallback(
    pool ?? undefined,
    token0,
    token1,
    config?.type as 'IslandPool' | 'V2Pool' | 'CharmPool'
  )

  useEffect(() => {
    const currentlyLoading = isLoading || (isUninitialized && !error)
    let timer: NodeJS.Timeout | undefined

    if (currentlyLoading !== stableLoadingState) {
      if (timer) clearTimeout(timer)
      const delay = currentlyLoading ? 800 : 200
      timer = setTimeout(() => {
        setStableLoadingState(currentlyLoading)
      }, delay)

      return () => {
        if (timer) clearTimeout(timer)
      }
    }
    return undefined
  }, [error, isLoading, isUninitialized, stableLoadingState])

  const {
    poolCurrency,
    userBalanceShares,
    userBalanceSharesUSD,
    userUnderlyingBalances,
    isLoading: positionLoading,
  } = useOptimizedPoolPositionDetails(pool ?? null, currencyA, currencyB, account, sharedBalances)

  const userKodiakFarmUnderlyingBalances = useOptimizedPoolUnderlyingBalances(
    pool ?? null,
    totalKodiakFarmLiquidity,
    sharedBalances,
    currencyA,
    currencyB
  )

  const rewardVaultStakedBalance = useMemo(() => {
    if (isRewardVaultActive && rewardVault && rewardVault.selfStaked) {
      return rewardVault.selfStaked
    }
    return undefined
  }, [isRewardVaultActive, rewardVault])

  const baultDepositedBalance = useMemo(() => {
    if (isRewardVaultActive && baultData?.estimatedLpTokens) {
      return baultData.estimatedLpTokens
    }
    return undefined
  }, [isRewardVaultActive, baultData])

  const rewardVaultUnderlyingBalances = useOptimizedPoolUnderlyingBalances(
    pool ?? null,
    rewardVaultStakedBalance,
    sharedBalances,
    currencyA,
    currencyB
  )

  const baultUnderlyingBalances = useOptimizedPoolUnderlyingBalances(
    pool ?? null,
    baultDepositedBalance,
    sharedBalances,
    currencyA,
    currencyB
  )

  const stakesForPositionCard = useMemo(() => {
    if (isKodiakFarmActive) {
      return kodiakFarmStakes || []
    } else if (isRewardVaultActive && rewardVault && rewardVault.positions) {
      return rewardVault.positions
    }
    return []
  }, [isKodiakFarmActive, isRewardVaultActive, kodiakFarmStakes, rewardVault])

  const combinedUserUnderlyingBalances = useMemo(() => {
    let amount0 = userUnderlyingBalances.amount0
    let amount1 = userUnderlyingBalances.amount1

    if (isKodiakFarmActive && userKodiakFarmUnderlyingBalances.amount0 && userKodiakFarmUnderlyingBalances.amount1) {
      amount0 = amount0
        ? amount0.add(userKodiakFarmUnderlyingBalances.amount0)
        : userKodiakFarmUnderlyingBalances.amount0
      amount1 = amount1
        ? amount1.add(userKodiakFarmUnderlyingBalances.amount1)
        : userKodiakFarmUnderlyingBalances.amount1
    }

    if (isRewardVaultActive && rewardVaultUnderlyingBalances.amount0 && rewardVaultUnderlyingBalances.amount1) {
      amount0 = amount0 ? amount0.add(rewardVaultUnderlyingBalances.amount0) : rewardVaultUnderlyingBalances.amount0
      amount1 = amount1 ? amount1.add(rewardVaultUnderlyingBalances.amount1) : rewardVaultUnderlyingBalances.amount1
    }

    if (baultUnderlyingBalances.amount0 && baultUnderlyingBalances.amount1) {
      amount0 = amount0 ? amount0.add(baultUnderlyingBalances.amount0) : baultUnderlyingBalances.amount0
      amount1 = amount1 ? amount1.add(baultUnderlyingBalances.amount1) : baultUnderlyingBalances.amount1
    }

    return { amount0, amount1 }
  }, [
    userUnderlyingBalances,
    isKodiakFarmActive,
    isRewardVaultActive,
    userKodiakFarmUnderlyingBalances,
    rewardVaultUnderlyingBalances,
    baultUnderlyingBalances,
  ])

  const combinedUserBalanceShares = useMemo(() => {
    if (!poolCurrency) return undefined

    let shares = CurrencyAmount.fromRawAmount(poolCurrency, '0')

    if (userBalanceShares?.currency.equals(poolCurrency)) {
      shares = shares.add(userBalanceShares)
    }

    if (isKodiakFarmActive && totalKodiakFarmLiquidity?.currency.equals(poolCurrency)) {
      shares = shares.add(totalKodiakFarmLiquidity)
    } else if (isRewardVaultActive && rewardVault?.userStaked?.currency.equals(poolCurrency)) {
      shares = shares.add(rewardVault.userStaked)
    }

    if (baultData?.estimatedLpTokens && poolCurrency) {
      shares = shares.add(baultData.estimatedLpTokens)
    }

    return shares
  }, [
    userBalanceShares,
    isKodiakFarmActive,
    totalKodiakFarmLiquidity,
    isRewardVaultActive,
    rewardVault?.userStaked,
    poolCurrency,
    baultData?.estimatedLpTokens,
  ])

  const combinedUserBalanceSharesUSD = useMemo(() => {
    let total = userBalanceSharesUSD || 0

    if (isKodiakFarmActive) {
      total += totalKodiakFarmLiquidityUSD || 0
    } else if (isRewardVaultActive) {
      total += rewardVault?.userStakedUSD || 0
    }

    if (baultData?.estimatedLpTokensUSD) {
      total += baultData.estimatedLpTokensUSD
    }

    return total
  }, [
    userBalanceSharesUSD,
    isKodiakFarmActive,
    totalKodiakFarmLiquidityUSD,
    isRewardVaultActive,
    rewardVault?.userStakedUSD,
    baultData?.estimatedLpTokensUSD,
  ])

  const displayFarmTvl = useMemo(() => {
    if (isKodiakFarmActive && kodiakFarm?.tvl !== undefined && kodiakFarm?.tvl !== null) {
      return kodiakFarm.tvl
    }
    if (isRewardVaultActive && rewardVault?.totalStakedUSD !== undefined && rewardVault?.totalStakedUSD !== null) {
      return rewardVault.totalStakedUSD
    }
    // Fallback to API value if hooks haven't loaded yet
    return metrics?.farmTvl
  }, [isKodiakFarmActive, kodiakFarm?.tvl, isRewardVaultActive, rewardVault?.totalStakedUSD, metrics?.farmTvl])

  const isPositionDataLoading = useMemo(() => {
    return isLoading || isFetching || isLoadingFarmLockedStakes || isLoadingRewardVault || baultLoading
  }, [isLoading, isFetching, isLoadingFarmLockedStakes, isLoadingRewardVault, baultLoading])

  const externalLinks = useMemo(() => {
    if (!config || !stakingData) return []

    return config.getExternalLinks({
      chainId: chainId!,
      poolApiResponse: pool ?? undefined,
      kodiakFarmAddress: stakingData.kodiakFarmAddress,
      rewardVaultAddress: stakingData.rewardVaultAddress,
      baultAddress: stakingData.baultAddress,
    })
  }, [config, stakingData, chainId, pool])

  const aprDisplay = useMemo(() => {
    if (!config || !stakingData) return null

    const result = config.getAprDisplay({
      isRewardVaultActive: !!stakingData.rewardVaultAddress,
      isKodiakFarmActive: !!stakingData.kodiakFarmAddress,
      rewardVaultRewardSymbol: rewardVault?.rewardToken?.symbol,
    })

    return result
  }, [config, stakingData, rewardVault?.rewardToken?.symbol])

  const contextValues = useMemo(() => {
    if (!pool || !currencyA || !currencyB || !tokenId || !config || !stakingData) {
      return null
    }

    return {
      currencyA,
      currencyB,
      wrappedNativeCurrency,
      feeAmount: poolData?.feeTier,
      farmAddress: stakingData.kodiakFarmAddress,
      poolType: config.type,
      islandId: tokenId as string,
      routerAddress,
      poolApiResponse: pool,
      stakingData,
      config,
      baultAddress: stakingData.baultAddress,
      rewardVault: stakingData.rewardVaultAddress,
      rewardVaultRewardToken: rewardVault?.rewardToken,
      isRewardVaultStakingDisabled,
    }
  }, [
    pool,
    currencyA,
    currencyB,
    tokenId,
    config,
    stakingData,
    wrappedNativeCurrency,
    poolData?.feeTier,
    routerAddress,
    rewardVault?.rewardToken,
    isRewardVaultStakingDisabled,
  ])

  const shouldShowCharmSpecificFeatures = useMemo(() => {
    return config?.type === 'Charm'
  }, [config?.type])

  const shouldShowYbgtBanner = useMemo(() => {
    return pool?.id === '0x5347e5133b22a680ee94b7e62803e848f8d8c92e' // YBGT pool address
  }, [pool?.id])

  const hasError = isError && error?.name !== 'UnsupportedChainId'

  if (account && !isSupportedChain(chainId)) {
    return <WrongNetworkCard />
  }

  const handleBackToPoolsList = () => {
    navigate('/liquidity/pools')
  }

  if (shouldShowLoading) {
    return (
      <S.Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader size="40px" />
        </div>
      </S.Wrapper>
    )
  }

  if (hasError) {
    return <PoolNotFoundUI error={error} poolId={tokenId} onRetry={refetch} onBackToPoolsList={handleBackToPoolsList} />
  }

  if (rawTokenId && !tokenId) {
    return (
      <PoolNotFoundUI
        error={{ message: 'Invalid address' }}
        poolId={rawTokenId}
        onBackToPoolsList={handleBackToPoolsList}
      />
    )
  }

  if (!config || !poolData || !stakingData || !metrics || !aprDisplay || contextValues === null) {
    return (
      <PoolNotFoundUI
        error={{ message: 'Pool not found' }}
        poolId={tokenId}
        onBackToPoolsList={handleBackToPoolsList}
      />
    )
  }

  return (
    <IslandProvider value={contextValues as IslandContextType}>
      <S.Wrapper>
        <StyledInternalLink
          data-cy="visit-islands"
          style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }}
          to={backPath}
        >
          <S.BackButton>
            <ArrowLeft color={theme.textSecondary} size={20} />
            {backLabel}
          </S.BackButton>
        </StyledInternalLink>

        {shouldShowMigration && <MigrationBanner rewardVaultRewardTokenSymbol={rewardVault?.rewardToken?.symbol} />}

        {shouldShowCharmSpecificFeatures && <CharmWarningBanner poolAddress={pool?.id || ''} chainId={chainId} />}
        {shouldShowYbgtBanner && <YbgtWarningBanner />}

        <S.IslandInfoCard>
          <HeaderWithPriceInfo processedData={processedData} tickAtLimit={tickAtLimit} poolType={config.type}>
            <S.IslandSelect
              disabled={false}
              visible={true}
              selected={true}
              hideInput={false}
              className="open-island-select-button"
            >
              <S.FlexRowCenter style={{ width: '100%' }}>
                {currencyA && currencyB && (
                  <>
                    <span style={{ marginRight: '0.5rem' }}>
                      <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} size={24} margin={true} />
                    </span>
                    <ThemedText.DeprecatedBody
                      fontSize="20px"
                      fontWeight="600"
                      lineHeight="20px"
                      color={theme.textPrimary}
                    >
                      {currencyA.symbol}-{currencyB.symbol}
                    </ThemedText.DeprecatedBody>
                  </>
                )}
                {poolData.feeTier && (
                  <StategyBadge>{new Percent(poolData.feeTier, 1_000_000).toSignificant()}%</StategyBadge>
                )}
                <StategyBadge>{config.type}</StategyBadge>
              </S.FlexRowCenter>
            </S.IslandSelect>
          </HeaderWithPriceInfo>

          <ExternalLinksSection externalLinks={externalLinks} />

          <PoolMetricsSection
            config={config}
            metrics={metrics}
            stakingData={stakingData}
            aprDisplay={aprDisplay}
            kodiakFarm={kodiakFarm ?? undefined}
            poolApiResponse={pool}
            displayFarmTvl={displayFarmTvl}
          />
        </S.IslandInfoCard>

        <S.FlexCol>
          <IslandAnalyticsCard />
        </S.FlexCol>

        <S.AnalyticsAndManagementFlex>
          <S.CardRowFlex>
            <S.FlexCol>
              <IslandPositionCard
                userBalanceShares={userBalanceShares}
                userBalanceSharesUSD={userBalanceSharesUSD}
                userUnderlyingBalances={userUnderlyingBalances}
                combinedUserBalanceShares={combinedUserBalanceShares}
                combinedUserBalanceSharesUSD={combinedUserBalanceSharesUSD}
                combinedUserUnderlyingBalances={combinedUserUnderlyingBalances}
                stakingPositions={{
                  kodiakFarm: isKodiakFarmActive
                    ? {
                        liquidity: totalKodiakFarmLiquidity,
                        liquidityUSD: totalKodiakFarmLiquidityUSD,
                        underlyingBalances: userKodiakFarmUnderlyingBalances,
                        stakes: stakesForPositionCard,
                      }
                    : undefined,

                  rewardVault:
                    isRewardVaultActive && rewardVault
                      ? {
                          liquidity: rewardVault.selfStaked,
                          liquidityUSD: rewardVault.userStakedUSD ?? undefined,
                          underlyingBalances: rewardVaultUnderlyingBalances,
                          stakes: rewardVault.positions,
                        }
                      : undefined,

                  bault: baultData
                    ? {
                        liquidity: baultData.estimatedLpTokens,
                        liquidityUSD: baultData.estimatedLpTokensUSD ?? undefined,
                        underlyingBalances: baultUnderlyingBalances,
                        positions: baultData.positions,
                      }
                    : undefined,
                }}
                poolCurrency={poolCurrency}
                currencyA={currencyA}
                currencyB={currencyB}
                token0={token0}
                token1={token1}
                isLoading={isPositionDataLoading}
              />

              {(isKodiakFarmActive || isRewardVaultActive) && (
                <IslandFarmEarnedCard
                  kodiakFarm={kodiakFarm}
                  rewardVault={isRewardVaultActive ? rewardVault : undefined}
                  stakingType={stakingData.stakingType}
                  shouldShowMigration={shouldShowMigration}
                  hasKodiakFarm={hasKodiakFarm}
                />
              )}
            </S.FlexCol>

            <IslandManagementCard />
          </S.CardRowFlex>
        </S.AnalyticsAndManagementFlex>
      </S.Wrapper>
      <PoweredByKodiak />
    </IslandProvider>
  )
}

export default React.memo(PoolPage)
